'use client';

import { useEffect, useRef } from 'react';
import { useCookiebotConsent } from '@/hooks/use-cookiebot-consent';

type Props = {
  html: string;
  className?: string;
  emptyPlaceholder?: string;
  /**
   * Cookie-consent category required to render this content (`'marketing'`, `'statistics'`, …).
   * `undefined`/`'none'` renders unconditionally. When set, the content (and its scripts) is
   * only injected once the visitor has granted that consent.
   */
  requiredConsent?: string;
  /** Studio preview: render regardless of consent so editors can see/edit the content. */
  bypassConsent?: boolean;
};

const containsScript = (html: string): boolean => /<script[\s>]/i.test(html);

/**
 * Renders raw, admin-authored HTML for the Custom HTML section **faithfully** — including
 * `<style>`, forms and inline styles, which the rich-text sanitizer would otherwise strip.
 *
 * When the markup contains a `<script>` (e.g. a MailerLite embedded form's `webforms.min.js`),
 * it's injected via `createContextualFragment` so the scripts actually run — `innerHTML` /
 * `dangerouslySetInnerHTML` parse `<script>` tags but never execute them.
 *
 * Rendering can be gated behind a cookie-consent category (`requiredConsent`) so third-party
 * embeds (which set cookies / track) don't load before the visitor consents.
 *
 * SECURITY: this executes whatever is pasted. Only expose the Custom HTML editor to trusted admins.
 */
export const CustomHtmlContent = ({
  html,
  className,
  emptyPlaceholder = 'Paste HTML in the section editor',
  requiredConsent,
  bypassConsent = false,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const hasConsent = useCookiebotConsent(requiredConsent);
  const canRender = bypassConsent || hasConsent;

  const hasScript = Boolean(html) && containsScript(html);
  const injectScript = hasScript && canRender;

  useEffect(() => {
    if (!injectScript) return;

    const container = ref.current;
    if (!container) return;

    container.innerHTML = '';
    container.appendChild(document.createRange().createContextualFragment(html));

    return () => {
      container.innerHTML = '';
    };
  }, [html, injectScript]);

  if (!html?.trim()) {
    return <div className={`text-zinc-400 ${className ?? ''}`}>{emptyPlaceholder}</div>;
  }

  // Consent required but not granted: render nothing (no markup, no scripts, no cookies).
  if (!canRender) {
    return null;
  }

  if (hasScript) {
    return <div ref={ref} className={className} />;
  }

  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
};
